import { useEffect, useState } from 'react';
import Panel from '../../components/Panel/Panel';
import useTranslator from '../../hooks/translate';
import { useAppSelector } from '../../hooks';
import { toServer } from '../../util/ServerSocket';
import './Rankings.css';
import { colorChatText } from '../Chat/ColoredChatText';
import { CDNImage } from '../../components/Elements/Image';

type Player = { rank: number; accountId: number; displayName: string; level: number; value: number;
  outfit: number | null; gender: 'male' | 'female' | null; village: number; online: boolean; nameColors?: string[]; nameNeon?: boolean };
type Result = { rows: Player[]; self: Player | null; total: number; pages: number; page: number; supported: boolean; error?: boolean };
const categories = ['level', 'power', 'arena', 'rta', 'guild', 'donation', 'lasNoches', 'jars', 'achievements', 'ninjaWar', 'weeklyBonus', 'weeklyBanknotes'];
const villageIcons: Record<number, string> = { 111: 'Leaf', 211: 'Mist', 311: 'Cloud', 411: 'Sand', 511: 'Rock' };

export function Rankings({ onClose }: { onClose: () => void }) {
  const t = useTranslator();
  const bounds = useAppSelector(state => state.ui.bounds);
  const serverId = useAppSelector(state => state.account.serverId);
  const [metric, setMetric] = useState('level');
  const [village, setVillage] = useState(0);
  const [page, setPage] = useState(1);
  const [jump, setJump] = useState('');
  const [revision, refresh] = useState(0);
  const [result, setResult] = useState<Result | null>(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let active = true;
    setResult(null); setFailed(false);
    const timeout = window.setTimeout(() => { if (active) setFailed(true); }, 10000);
    toServer('gameRankings', { metric, village, page }, (data: Result) => {
      if (!active) return;
      window.clearTimeout(timeout);
      if (data?.error || !Array.isArray(data?.rows)) { setFailed(true); return; }
      setFailed(false); setResult(data);
    });
    return () => { active = false; window.clearTimeout(timeout); };
  }, [metric, village, page, revision]);
  const pages = result?.pages ?? 1;
  const currentPage = result?.page ?? page;
  const valueLabel = metric === 'level' ? 'Level' : `game_rank__value_${metric}`;
  const changePage = (next: number) => setPage(Math.max(1, Math.min(pages, next)));
  const outfitName = (id: number | null) => {
    if (!id) return '—';
    const key = `item__${id}--name`, label = t(key);
    return label === key ? '—' : label;
  };
  const playerName = (player: Player) => <button className="game-rankings-name" aria-label={player.displayName} title={player.displayName} onClick={() => toServer('viewCharacter', { serverId, accountId: player.accountId })}>{colorChatText(player.displayName, player.nameColors, player.nameNeon)}</button>;
  return <Panel name={t('game_rank__title')} onClose={onClose}>
    <div className="game-rankings" style={{ width: Math.max(540, Math.min(840, bounds[2] - 48)), height: Math.max(340, Math.min(460, bounds[3] - 100)) }}>
      <nav className="game-rankings-nav" aria-label={t('game_rank__categories')}>
        {categories.map(category => <button key={category} aria-pressed={metric === category} title={t(`game_rank__cat_${category}`)} onClick={() => { setMetric(category); setPage(1); setJump(''); }}>
          {t(`game_rank__cat_${category}`)}
        </button>)}
      </nav>
      <section className="game-rankings-main">
        <div className="game-rankings-controls">
          <span>{t(`game_rank__cat_${metric}`)}</span>
          <select aria-label={t('game_rank__village')} value={village} onChange={e => { setVillage(Number(e.target.value)); setPage(1); }}>
            <option value={0}>{t('game_rank__all')}</option>
            {[111, 211, 311, 411, 511].map(id => <option key={id} value={id}>{t(`scene__${id}--name`)}</option>)}
          </select>
          <button onClick={() => refresh(value => value + 1)}>{t('Refresh')}</button>
        </div>
        <div className="game-rankings-table">
          <table><thead><tr>
            <th>{t('game_rank__rank')}</th><th title={metric === 'power' ? t('game_rank__power_formula') : undefined}>{t(valueLabel)}</th>
            <th>{t('game_rank__player')}</th><th>{t('game_rank__outfit')}</th><th>{t('game_rank__gender')}</th><th>{t('game_rank__village')}</th>
          </tr></thead>
            <tbody>{!failed && result?.supported !== false && result?.rows.map(player => <tr key={player.accountId} className={result.self?.accountId === player.accountId ? 'is-self' : ''}>
              <td>{player.rank}</td><td>{(metric === 'level' ? player.level : player.value).toLocaleString()}</td>
              <td>{playerName(player)} <small className={player.online ? 'rank-online' : 'rank-offline'}>{player.online ? 'on' : 'off'}</small></td>
              <td title={outfitName(player.outfit)}>{outfitName(player.outfit)}</td>
              <td className={`rank-gender ${player.gender ?? ''}`} title={player.gender ? t(`game_rank__${player.gender}`) : t('game_rank__unknown_gender')}>{player.gender === 'female' ? '♀' : player.gender === 'male' ? '♂' : '—'}</td>
              <td>{villageIcons[player.village] ? <CDNImage className="rank-village-icon" src={`scenes/newPlayer/village${villageIcons[player.village]}.png`} alt={t(`scene__${player.village}--name`)} title={t(`scene__${player.village}--name`)} /> : '—'}</td>
            </tr>)}</tbody>
          </table>
          {failed ? <p role="alert">{t('game_rank__error')}</p> : !result ? <p role="status">{t('game_rank__loading')}</p> : result.supported === false ?
            <div className="game-rankings-empty"><strong>{t(`game_rank__cat_${metric}`)}</strong><p>{t('game_rank__unavailable')}</p></div> : !result.rows.length ? <p>{t('game_rank__empty')}</p> : null}
        </div>
        <footer className="game-rankings-footer">
          <div className="game-rankings-pagination">
            <button aria-label={t('game_rank__first')} disabled={!result || currentPage <= 1} onClick={() => changePage(1)}>⏮</button>
            <button aria-label={t('game_rank__previous')} disabled={!result || currentPage <= 1} onClick={() => changePage(currentPage - 1)}>◀</button>
            <b>{currentPage}/{pages}</b>
            <button aria-label={t('game_rank__next')} disabled={!result || currentPage >= pages} onClick={() => changePage(currentPage + 1)}>▶</button>
            <button aria-label={t('game_rank__last')} disabled={!result || currentPage >= pages} onClick={() => changePage(pages)}>⏭</button>
            <form onSubmit={e => { e.preventDefault(); if (/^\d+$/.test(jump)) changePage(Number(jump)); }}>
              <input aria-label={t('game_rank__page')} type="number" min={1} max={pages} value={jump} onChange={e => setJump(e.target.value)} />
              <button className="rank-go" type="submit" disabled={!result}>GO!</button>
            </form>
          </div>
          <p className="game-rankings-note">{metric === 'ninjaWar' ? t('game_rank__ninja_war_note') : metric === 'donation' ? t('game_rank__donation_note') : metric === 'power' ? t('game_rank__power_formula') : metric === 'lasNoches' ? t('game_rank__tower_note') : t('game_rank__top100')}</p>
          {result?.supported && <p>{t('game_rank__self')}: {result.self ? <>#{result.self.rank} · {playerName(result.self)}</> : '—'}</p>}
        </footer>
      </section>
    </div>
  </Panel>;
}
